import React from 'react';

export const FLOW_DESIGNER_SIDEBAR_DEFAULT_WIDTH = 380;
export const FLOW_DESIGNER_SIDEBAR_MIN_WIDTH = 320;
export const FLOW_DESIGNER_SIDEBAR_MAX_WIDTH = 640;

const FLOW_DESIGNER_SIDEBAR_WIDTH_STORAGE_KEY = 'flow-designer-sidebar-width';
const KEYBOARD_RESIZE_STEP = 16;
const KEYBOARD_RESIZE_LARGE_STEP = 48;

type ResizeStart = {
  startWidth: number;
  startX: number;
};

type ResizeHandleProps = {
  onPointerDown: React.PointerEventHandler<HTMLDivElement>;
  onKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
};

export function clampSidebarWidth(width: number): number {
  if (!Number.isFinite(width)) return FLOW_DESIGNER_SIDEBAR_DEFAULT_WIDTH;
  return Math.min(
    FLOW_DESIGNER_SIDEBAR_MAX_WIDTH,
    Math.max(FLOW_DESIGNER_SIDEBAR_MIN_WIDTH, Math.round(width))
  );
}

function readStoredSidebarWidth(): number {
  if (typeof window === 'undefined') return FLOW_DESIGNER_SIDEBAR_DEFAULT_WIDTH;

  try {
    const stored = window.localStorage.getItem(FLOW_DESIGNER_SIDEBAR_WIDTH_STORAGE_KEY);
    if (!stored) return FLOW_DESIGNER_SIDEBAR_DEFAULT_WIDTH;

    return clampSidebarWidth(Number(stored));
  } catch (error) {
    console.error('Failed to load Flow Designer sidebar width:', error);
    return FLOW_DESIGNER_SIDEBAR_DEFAULT_WIDTH;
  }
}

function persistSidebarWidth(width: number): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(FLOW_DESIGNER_SIDEBAR_WIDTH_STORAGE_KEY, String(width));
  } catch (error) {
    console.error('Failed to save Flow Designer sidebar width:', error);
  }
}

export function useResizableSidebarWidth(): {
  width: number;
  minWidth: number;
  maxWidth: number;
  isResizing: boolean;
  resizeHandleProps: ResizeHandleProps;
} {
  const [width, setWidthState] = React.useState(readStoredSidebarWidth);
  const [isResizing, setIsResizing] = React.useState(false);
  const widthRef = React.useRef(width);
  const cleanupResizeRef = React.useRef<(() => void) | null>(null);

  React.useEffect(() => {
    widthRef.current = width;
  }, [width]);

  const setWidth = React.useCallback((nextWidth: number) => {
    const clampedWidth = clampSidebarWidth(nextWidth);
    setWidthState(clampedWidth);
    widthRef.current = clampedWidth;
    persistSidebarWidth(clampedWidth);
  }, []);

  const stopResize = React.useCallback(() => {
    cleanupResizeRef.current?.();
    cleanupResizeRef.current = null;
    setIsResizing(false);
  }, []);

  const handlePointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;

      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);

      cleanupResizeRef.current?.();

      const resizeStart: ResizeStart = {
        startWidth: widthRef.current,
        startX: event.clientX,
      };
      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      setIsResizing(true);

      const handlePointerMove = (moveEvent: PointerEvent) => {
        setWidth(resizeStart.startWidth + resizeStart.startX - moveEvent.clientX);
      };

      const handlePointerUp = () => {
        stopResize();
      };

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerUp);

      cleanupResizeRef.current = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        window.removeEventListener('pointercancel', handlePointerUp);
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
      };
    },
    [setWidth, stopResize]
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const step = event.shiftKey ? KEYBOARD_RESIZE_LARGE_STEP : KEYBOARD_RESIZE_STEP;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setWidth(widthRef.current + step);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        setWidth(widthRef.current - step);
      } else if (event.key === 'Home') {
        event.preventDefault();
        setWidth(FLOW_DESIGNER_SIDEBAR_MIN_WIDTH);
      } else if (event.key === 'End') {
        event.preventDefault();
        setWidth(FLOW_DESIGNER_SIDEBAR_MAX_WIDTH);
      }
    },
    [setWidth]
  );

  React.useEffect(
    () => () => {
      cleanupResizeRef.current?.();
    },
    []
  );

  return {
    width,
    minWidth: FLOW_DESIGNER_SIDEBAR_MIN_WIDTH,
    maxWidth: FLOW_DESIGNER_SIDEBAR_MAX_WIDTH,
    isResizing,
    resizeHandleProps: {
      onPointerDown: handlePointerDown,
      onKeyDown: handleKeyDown,
    },
  };
}
